import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, FilePlus2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, downloadBlob, errorMessage } from "@/lib/api";
import { money } from "@/lib/pricing";
import { StageBadge, ScopeFilters, usePeople } from "@/lib/crm";
import { fmtDate } from "@/pages/Dashboard";

const matches = (q, f) => (!f.q || `${q.number} ${q.client_name} ${q.project_name}`.toLowerCase().includes(f.q.toLowerCase()))
  && (!f.stage || q.stage === f.stage) && (!f.showroom_id || q.showroom_id === f.showroom_id) && (!f.rep_id || q.rep_id === f.rep_id)
  && (!f.date_from || q.created_at.slice(0, 10) >= f.date_from) && (!f.date_to || q.created_at.slice(0, 10) <= f.date_to);

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const people = usePeople();
  const [f, setF] = useState({});

  const load = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch((e) => { toast.error(errorMessage(e)); setQuotes([]); });
  useEffect(() => { load(); }, []);

  const pdf = async (e, q) => {
    e.stopPropagation();
    try {
      await downloadBlob(`/quotes/${q.id}/pdf`, `Estimate-${q.number}-${q.client_name}.pdf`.replace(/[^\w.-]+/g, "-"));
    } catch (err) {
      toast.error(errorMessage(err, "Could not generate the PDF"));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/quotes/${toDelete.id}`);
      toast.success("Quote deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
    setToDelete(null);
  };

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-50">Quotes</h1>
          <p className="mt-2 text-sm text-slate-400">All saved quotes for your company, newest first.</p>
        </div>
        <Link to="/dashboard" data-testid="quotes-new-button" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400">
          <FilePlus2 className="h-4 w-4" /> New quote
        </Link>
      </div>
      <ScopeFilters people={people} f={f} setF={setF} prefix="quotes" stages />
      <div className="rise rise-1 overflow-x-auto rounded-2xl border border-slate-800 bg-[#F2F2F7]/60">
        <table className="w-full min-w-[720px]" data-testid="saved-quotes-table">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3">Quote</th><th className="px-6 py-3">Client</th><th className="px-6 py-3">Project</th><th className="px-6 py-3">Stage</th>
              <th className="px-6 py-3">Date</th><th className="px-6 py-3">Sales rep</th><th className="px-6 py-3 text-right">Final total</th><th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotes?.filter((x) => matches(x, f)).map((q, i) => (
              <tr key={q.id} data-testid={`saved-quote-row-${i}`} onClick={() => navigate(`/quotes/${q.id}`)}
                className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-900/50">
                <td className="px-6 py-4 font-mono-num text-xs text-slate-200" data-testid={`saved-quote-number-${i}`}>{q.number}</td>
                <td className="px-6 py-4 text-sm font-medium text-slate-100" data-testid={`saved-quote-client-${i}`}>{q.client_name}</td>
                <td className="px-6 py-4 text-sm text-slate-300" data-testid={`saved-quote-project-${i}`}>{q.project_name || "—"} <span className="text-xs text-slate-500">v{q.version}</span></td>
                <td className="px-6 py-4"><StageBadge stage={q.stage} testId={`saved-quote-stage-${i}`} /></td>
                <td className="px-6 py-4 text-sm text-slate-400" data-testid={`saved-quote-date-${i}`}>{fmtDate(q.created_at)}</td>
                <td className="px-6 py-4 text-sm text-slate-300" data-testid={`saved-quote-creator-${i}`}>{q.rep_name || q.created_by_name || "—"}</td>
                <td className="px-6 py-4 text-right font-mono-num text-sm font-semibold text-amber-300" data-testid={`saved-quote-total-${i}`}>{money(q.final_total)}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`saved-quote-pdf-button-${i}`} onClick={(e) => pdf(e, q)} title="Download PDF"
                      className="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-amber-300"><Download className="h-4 w-4" /></button>
                    <button data-testid={`saved-quote-delete-button-${i}`} onClick={(e) => { e.stopPropagation(); setToDelete(q); }} title="Delete"
                      className="rounded-md p-2 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {quotes?.length === 0 && <p data-testid="quotes-empty" className="py-16 text-center text-sm text-slate-500">No quotes yet. Upload a drawing to create your first one.</p>}
        {quotes?.length > 0 && !quotes.some((x) => matches(x, f)) && <p data-testid="quotes-no-match" className="py-16 text-center text-sm text-slate-500">No matching quotes.</p>}
        {quotes === null && <div className="h-32 animate-pulse bg-slate-900/40" />}
      </div>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-slate-800 bg-[#F2F2F7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quote {toDelete?.number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the quote for {toDelete?.client_name}. It can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-quote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-quote-confirm" onClick={remove} className="bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
